import { groupBy } from 'es-toolkit/array';
import { isLeft } from 'fp-ts/Either';
import { useEffect, type Dispatch, type SetStateAction } from 'react';
import type { TRPC } from '../contexts/TRPC';
import type { RightResult, RouterOutputs } from '../types/TRPC';
import { captureAndReportError } from '../utils/errorReporting';
import { useAsyncData } from './useAsyncData';

export type RejectionReasonOption = RightResult<
  RouterOutputs['listRejectionReasons']
>[number];

export interface UseRejectionReasonsResult {
  rejectionReasonOptions: RejectionReasonOption[];
  isRejectionReasonsLoading: boolean;
  rejectionReasonsError: string | null;
  setRejectionReasonOptions: Dispatch<SetStateAction<RejectionReasonOption[]>>;
}

// Server order (category asc, code asc) is preserved: groupBy + Object.entries
// keep insertion order.
export function groupRejectionReasonsByCategory(
  reasons: RejectionReasonOption[],
): Array<{ category: string; items: RejectionReasonOption[] }> {
  return Object.entries(groupBy(reasons, (reason) => reason.category)).map(
    ([category, items]) => ({ category, items }),
  );
}

export function useRejectionReasons(
  trpc: TRPC | null,
): UseRejectionReasonsResult {
  const {
    data: rejectionReasonOptions,
    setData: setRejectionReasonOptions,
    isLoading: isRejectionReasonsLoading,
    error: rejectionReasonsError,
    run,
  } = useAsyncData<RejectionReasonOption[]>({
    initialData: [],
    initialLoading: true,
  });

  useEffect(() => {
    if (!trpc?.client) {
      return;
    }
    const client = trpc.client;

    void run(() => client.listRejectionReasons.query(undefined), {
      onSuccess: (result, { setData, setError }) => {
        if (isLeft(result)) {
          setError(result.left.message || 'Failed to load rejection reasons');
          return;
        }
        setData(result.right);
      },
      onError: (loadError, { setError }) => {
        captureAndReportError('Error loading rejection reasons:', loadError);
        setError('Failed to load rejection reasons');
      },
    });
  }, [trpc, run]);

  return {
    rejectionReasonOptions,
    isRejectionReasonsLoading,
    rejectionReasonsError,
    setRejectionReasonOptions,
  };
}
